import * as React from "react"
import { FieldValues, FormProvider, useFormContext, UseFormReturn } from "react-hook-form"
import { Box } from "@chakra-ui/react"
import * as Sentry from "@sentry/nextjs"

import { useToast } from "lib/hooks/useToast"

interface FormContainerProps {
  onSubmit?: (values: any) => Promise<any> | any
  onBlur?: (values: any) => Promise<any> | any
  id?: string
  children: React.ReactNode
}

const FormContainer: React.FC<FormContainerProps> = (props) => {
  const toast = useToast()
  const { handleSubmit } = useFormContext()
  const onSubmit = async (values: any) => {
    try {
      if (props.onBlur) {
        return await props.onBlur(values)
      }
      if (props.onSubmit) {
        return await props.onSubmit(values)
      }
    } catch (e) {
      Sentry.captureException(e)
      console.error(e)
      toast({ title: "Network error", status: "error" })
      return
    }
  }

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && props.onSubmit) {
      const target = e.target as HTMLElement
      // Allow Enter to work normally in textareas (for line breaks)
      // Allow Enter to submit when pressed on submit button
      // Allow Enter in input fields to submit the form (standard HTML form behavior)
      // Prevent form submission when Enter is pressed on checkboxes or other non-input elements
      const isTextarea = target.tagName === "TEXTAREA"
      const isSubmitButton = target.tagName === "BUTTON" && target.getAttribute("type") === "submit"
      const isInput = target.tagName === "INPUT" && (target as HTMLInputElement).type !== "checkbox" && (target as HTMLInputElement).type !== "radio"
      
      // Only prevent default for non-input, non-textarea, non-submit-button elements
      if (!isTextarea && !isSubmitButton && !isInput) {
        e.preventDefault()
      }
    }
  }, [props.onSubmit])

  return (
    <Box
      as="form"
      w="100%"
      {...(props.id && { id: props.id })}
      {...(props.onSubmit && { onSubmit: handleSubmit(onSubmit) })}
      {...(props.onBlur && { onBlur: handleSubmit(onSubmit) })}
      onKeyDown={handleKeyDown}
    >
      {props.children}
    </Box>
  )
}

interface Props<T extends FieldValues> extends UseFormReturn<T>, FormContainerProps {
  children: React.ReactNode
}

export function Form<T extends FieldValues>({ onSubmit, onBlur, id, ...props }: Props<T>) {
  return (
    <FormProvider {...props}>
      <FormContainer {...{ onSubmit, onBlur, id }}>{props.children}</FormContainer>
    </FormProvider>
  )
}
